/* eslint-disable @typescript-eslint/no-explicit-any */
import styles from './FJGoogleDriveImportModal.module.less';
import useLocalStore from '../../../hooks/useLocalStore.js';
import FJLocalStore from '../../../store/FJLocalStore.js';
import FJModal from '../FJModal';
import FJModalClose from '../FJModalClose/FJModalClose';
import FJGoogleDriveIcon from '../../../assets//google-drive/google-drive.svg?react';
import { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import FJButton from '../../../common/components/FJButton/FJButton.tsx';
import FJMediaImportContent from './media/FJMediaImportContent.js';
import classNames from 'classnames';
import { FJMediaImportListStatus, FJMediaImportModelType } from '../../../constants/FJMediaImportConstant.js';
import { SHARE_BINDING_STATUS, SOCIAL_KEY } from '../../../constants/FJShareSocialConstant.js';
import FJBackButton from './FJBackButton.jsx';
import { uniqBy } from 'lodash-es';
import FJUserUtil from '../../../util/FJUserUtil';
import FJSocialCloud from '@src/store/FJGoogleDriveImportStore.ts';
import { useAppDispatch, useAppSelector } from '@src/redux/hooks.ts';
import { useSelector } from 'react-redux';
import { FJModalsAction } from '@src/redux/action/FJModalsAction.ts';
import FJGoogleDriveAction from '@src/redux/action/FJGoogleDriveAction.ts';

const FJGoogleDriveImportModal = () => {
    useLocalStore();
    const dispatch = useAppDispatch();
    const isVisible = useAppSelector(state => state.modal.googleDrive.isShow);
    const [selectedFileId, setSelectedFileId] = useState('');

    const linkAccountState: string = useSelector((state: any) => state.share.googleDrive.linkAccountState);
    const [pageToken, setPageToken] = useState(null);
    const [folder, setFolder] = useState([{ folder: 'root', name: 'Google' }]);
    const [models, setModels] = useState([]);
    const [status, setStatus] = useState(FJMediaImportListStatus.init);
    const [retryFlag, setRetryFlag] = useState(0);

    const currentFolder = folder[folder.length - 1];
    const isLinked = linkAccountState === SHARE_BINDING_STATUS.LINKED;
    const nextPageTokenRef = useRef(null);

    useEffect(() => {
        let abortController: any;
        if (isLinked && isVisible) {
            abortController = new AbortController();
            // eslint-disable-next-line react-hooks/set-state-in-effect
            setStatus(FJMediaImportListStatus.loading);
            const access_token = FJUserUtil.getAccessTokenWithType(SOCIAL_KEY.GOOGLE_DRIVE);
            FJSocialCloud.getFiles(
                SOCIAL_KEY.GOOGLE_DRIVE,
                access_token,
                currentFolder.folder,
                pageToken,
                abortController.signal,
            )
                .then(({ newModels, nextPageToken }) => {
                    const validModels = newModels.filter(
                        model => model.modelType === FJMediaImportModelType.folder || model.type === 'VIDEO',
                    );
                    setModels(models => uniqBy([...models, ...validModels], m => m.id));
                    nextPageTokenRef.current = nextPageToken;
                    setStatus(FJMediaImportListStatus.success);
                })
                .catch(e => {
                    if (e === 'IMPORT_MEDIA_AUTH_ERROR') {
                        dispatch(FJGoogleDriveAction.unlinkAction());
                    }
                    setStatus(FJMediaImportListStatus.failed);
                });
        }
        return () => {
            abortController?.abort();
        };
    }, [folder, pageToken, linkAccountState, retryFlag, isVisible]);

    const _resetPageState = () => {
        setModels([]);
        setPageToken(null);
        setStatus(FJMediaImportListStatus.init);
        setSelectedFileId('');
        nextPageTokenRef.current = null;
    };

    const _onClickFolder = (model: any) => {
        if (model.modelType !== FJMediaImportModelType.folder) return;
        _resetPageState();
        setFolder(folder => [...folder, model]);
    };

    const _loadMoreMete = () => {
        if (status === FJMediaImportListStatus.loading) {
            return;
        }

        if (nextPageTokenRef.current) {
            setPageToken(nextPageTokenRef.current);
        }
    };

    const onRetry = () => {
        setRetryFlag(retryFlag => retryFlag + 1);
    };

    const _onClose = () => {
        dispatch(FJModalsAction.hideGoogleDriveImportModalAction());
    };

    const _onSelect = () => {
        const selectedModel = models.filter((model: any) => selectedIds.includes(model.id))[0];
        dispatch(FJGoogleDriveAction.selectGoogleDriveFileAction(selectedModel));
    };

    const _handleGoBack = () => {
        _resetPageState();
        setFolder(folder => folder.slice(0, -1));
    };

    // eslint-disable-next-line react-hooks/preserve-manual-memoization
    const selectedIds = useMemo(() => {
        return [selectedFileId].filter(Boolean);
    }, [selectedFileId]);

    const handleClickFile = useCallback(
        (id: string) => {
            if (selectedFileId === id) {
                setSelectedFileId('');
            } else {
                setSelectedFileId(id);
            }
        },
        [selectedFileId],
    );

    const handleDoubleClickFile = useCallback(
        id => {
            setSelectedFileId(id);
            const selectedModel = models.find((model: any) => model.id === id);
            dispatch(FJGoogleDriveAction.selectGoogleDriveFileAction(selectedModel));
        },
        [models],
    );

    const currentFolderIsNotRoot = currentFolder.folder !== 'root';

    const userName =
        FJUserUtil.getAccountDataForUser(window.fjuser?.info?.account_link, SOCIAL_KEY.GOOGLE_DRIVE)?.user_name ?? '';

    return (
        <FJModal visible={isVisible} width={660} height={610} zIndex={1004}>
            <div className={styles.container} data-auto-id='APP_Modal_GOOGLE_DRIVE_IMPORT'>
                <FJModalClose onClick={_onClose} />
                <div className={styles.contentContainer}>
                    <div className={styles.header}>
                        <FJGoogleDriveIcon className={styles.icon} />
                        <div className={styles.titleContainer}>
                            <div className={styles.title}>{FJLocalStore._('google-drive')}</div>
                            <div className={styles.email}>{userName}</div>
                        </div>
                    </div>
                    <div className={styles.content}>
                        {currentFolderIsNotRoot && <FJBackButton onBack={_handleGoBack} className={styles.back} />}
                        <FJMediaImportContent
                            showDuration
                            supportSelect
                            selectedFileIds={selectedIds}
                            className={classNames(styles.importContentContainer, {
                                [styles.currentFolderIsNotRoot]: currentFolderIsNotRoot,
                            })}
                            folderClassName={styles.folderItem}
                            fileClassName={styles.fileItem}
                            folderSwitchClassName={styles.folderSwitch}
                            fileSwitchClassName={styles.fileSwitch}
                            fileWrapperClassName={styles.fileWrapper}
                            folderListClassName={styles.folderList}
                            fileListClassName={styles.fileList}
                            models={models}
                            status={status}
                            onTotalScroll={_loadMoreMete}
                            onClickFolder={_onClickFolder}
                            onRetry={onRetry}
                            onClickFile={handleClickFile}
                            onDoubleClickFile={handleDoubleClickFile}
                        />
                    </div>
                    <div className={styles.footer}>
                        <FJButton
                            onClick={_onClose}
                            className={classNames(styles.btn, styles.cancel)}
                            type='default'
                            text={FJLocalStore._('cancel')}
                        />
                        <FJButton
                            disabled={selectedIds.length === 0}
                            onClick={_onSelect}
                            className={classNames(styles.btn, styles.select)}
                            type='gradient'
                            text={FJLocalStore._('select')}
                        />
                    </div>
                </div>
            </div>
        </FJModal>
    );
};

export default FJGoogleDriveImportModal;
